import { SetDeviceResponse, SetDraftResponse, SetTrayResponse } from 'api/Responses/SetResponses';
import { Tag } from 'components/ReferenceImageGenerator/molecules/ImageGeneratorStage';
import { Region } from 'store/api/apiGenerated';

// -------------------------------------------------------------------------------------------------------------------
// Action Types
// -------------------------------------------------------------------------------------------------------------------
export type SetAction =
  | {
      type: 'INIT';
      payload: SetDraftResponse;
    }
  | {
      type: 'CODE' | 'REGION' | 'JME' | 'REV' | 'NAME';
      payload: string;
    }
  | {
      type: 'SET_MANUAL';
      payload: { filename: string; region: Region };
    }
  | {
      type: 'TRAY_ORIGINAL_IMAGE' | 'TRAY_REF' | 'TRAY_NAME' | 'TRAY_ADDITIONAL_INSPECTION';
      payload: string;
      trayID: string;
    }
  | {
      type: 'TRAY_NON_STANDARD';
      payload: boolean;
      trayID: string;
    }
  | {
      type: 'TRAY_GENERAL_TEST';
      payload: boolean;
      trayID: string;
    }
  | {
      type: 'TRAY_NEW';
      payload: SetTrayResponse;
    }
  | {
      type: 'TRAY_IMAGE';
      payload: { image: string; originalImage: string; referenceTags: Tag[] };
      trayID: string;
    }
  | {
      type: 'TRAY_REFERENCE_TAGS';
      payload: Tag[];
      trayID: string;
    }
  | {
      type: 'TRAY_DELETE';
      trayID: string;
    }
  | {
      type: 'PART_REF' | 'JAPANESE_ADDITIONAL_INSPECTION' | 'ADDITIONAL_INSPECTION' | 'PART_QTY';
      payload: string;
      trayID: string;
      partID: string;
    }
  | {
      type: 'PART_NEW';
      payload: SetDeviceResponse;
      trayID: string;
    }
  | {
      type: 'PART_DELETE';
      partID: string;
      trayID: string;
    }
  | {
      type: 'PART_DELETE_MANY';
      partIDs: string[];
      trayID: string;
    }
  | {
      type: 'PART_MOVE';
      partIDs: string[];
      fromTrayID: string;
      toTrayID: string;
    }
  | {
      type: 'PART_DEBRIS_INSPECTION' | 'PART_FUNCTION_TEST' | 'PART_TORQUE_TEST' | 'PART_PRONE_TO_DEBRIS';
      payload: boolean;
      trayID: string;
      partID: string;
    };

// -------------------------------------------------------------------------------------------------------------------
// Action Functions
// -------------------------------------------------------------------------------------------------------------------
export const draftActions = {
  // -------------------------------------------------------------------------------------------------------------------
  // Initialisation
  // -------------------------------------------------------------------------------------------------------------------
  init: (draft: SetDraftResponse): SetAction => ({
    type: 'INIT',
    payload: draft,
  }),
  // -------------------------------------------------------------------------------------------------------------------
  // Set actions
  // -------------------------------------------------------------------------------------------------------------------
  code: (code: string): SetAction => ({
    type: 'CODE',
    payload: code,
  }),
  region: (region: string): SetAction => ({
    type: 'REGION',
    payload: region,
  }),
  revision: (revision: string): SetAction => ({
    type: 'REV',
    payload: revision,
  }),
  jme: (jme: string): SetAction => ({
    type: 'JME',
    payload: jme,
  }),
  setManuals: (filename: string, region: Region): SetAction => ({
    type: 'SET_MANUAL',
    payload: { filename, region },
  }),
  name: (name: string): SetAction => ({
    type: 'NAME',
    payload: name,
  }),
  // -------------------------------------------------------------------------------------------------------------------
  // Tray actions
  // -------------------------------------------------------------------------------------------------------------------
  trayImage: (image: string, originalImage: string, referenceTags: Tag[], trayID: string): SetAction => ({
    type: 'TRAY_IMAGE',
    payload: { image, originalImage, referenceTags },
    trayID: trayID,
  }),
  trayOriginalImage: (image: string, trayID: string): SetAction => ({
    type: 'TRAY_ORIGINAL_IMAGE',
    payload: image,
    trayID: trayID,
  }),
  trayReferenceTags: (tags: Tag[], trayID: string): SetAction => ({
    type: 'TRAY_REFERENCE_TAGS',
    payload: tags,
    trayID: trayID,
  }),
  trayRef: (ref: string, trayID: string): SetAction => ({
    type: 'TRAY_REF',
    payload: ref,
    trayID: trayID,
  }),
  trayName: (name: string, trayID: string): SetAction => ({
    type: 'TRAY_NAME',
    payload: name,
    trayID: trayID,
  }),
  trayStandard: (nonStandard: boolean, trayID: string): SetAction => ({
    type: 'TRAY_NON_STANDARD',
    payload: nonStandard,
    trayID: trayID,
  }),
  trayGeneralTest: (generalTest: boolean, trayID: string): SetAction => ({
    type: 'TRAY_GENERAL_TEST',
    payload: generalTest,
    trayID: trayID,
  }),
  trayNew: (tray: SetTrayResponse): SetAction => ({
    type: 'TRAY_NEW',
    payload: tray,
  }),
  trayDelete: (trayID: string): SetAction => ({
    type: 'TRAY_DELETE',
    trayID,
  }),
  trayAdditionalInspection: (inspectionText: string, trayID: string): SetAction => ({
    type: 'TRAY_ADDITIONAL_INSPECTION',
    payload: inspectionText,
    trayID: trayID,
  }),
  // -------------------------------------------------------------------------------------------------------------------
  // Part actions
  // -------------------------------------------------------------------------------------------------------------------
  partRef: (ref: string, trayID: string, partID: string): SetAction => ({
    type: 'PART_REF',
    payload: ref,
    trayID,
    partID,
  }),
  partQuantity: (quantity: string, trayID: string, partID: string): SetAction => ({
    type: 'PART_QTY',
    payload: quantity,
    trayID,
    partID,
  }),
  partAdditionalInspection: (notes: string, trayID: string, partID: string, isJapanese: boolean): SetAction => ({
    type: isJapanese ? 'JAPANESE_ADDITIONAL_INSPECTION' : 'ADDITIONAL_INSPECTION',
    payload: notes,
    trayID,
    partID,
  }),
  partNew: (part: SetDeviceResponse, trayID: string): SetAction => ({
    type: 'PART_NEW',
    payload: part,
    trayID,
  }),
  partDelete: (trayID: string, partID: string): SetAction => ({
    type: 'PART_DELETE',
    partID,
    trayID,
  }),
  partDeleteMany: (trayID: string, partIDs: string[]): SetAction => ({
    type: 'PART_DELETE_MANY',
    partIDs,
    trayID,
  }),
  partMove: (fromTrayID: string, toTrayID: string, partIDs: string[]): SetAction => ({
    type: 'PART_MOVE',
    partIDs,
    fromTrayID,
    toTrayID,
  }),
  partDebrisInspection: (debrisInspection: boolean, trayID: string, partID: string): SetAction => ({
    type: 'PART_DEBRIS_INSPECTION',
    payload: debrisInspection,
    partID,
    trayID,
  }),
  partFunctionTest: (functionTest: boolean, trayID: string, partID: string): SetAction => ({
    type: 'PART_FUNCTION_TEST',
    payload: functionTest,
    partID,
    trayID,
  }),
  partTorqueTest: (torqueTest: boolean, trayID: string, partID: string): SetAction => ({
    type: 'PART_TORQUE_TEST',
    payload: torqueTest,
    partID,
    trayID,
  }),
  partProneToDebris: (proneToDebris: boolean, trayID: string, partID: string): SetAction => ({
    type: 'PART_PRONE_TO_DEBRIS',
    payload: proneToDebris,
    partID,
    trayID,
  }),
};
